import { useEffect, useRef, useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import {
  LayoutDashboard, MonitorDot, ListTree, FolderArchive, Filter,
  DatabaseBackup, Settings2, Map, ShieldCheck, LogOut, MoreHorizontal, X,
  Sun, Moon, MonitorSmartphone,
  PanelLeftClose, PanelLeftOpen, Bot,
} from 'lucide-react';
import { useAuth } from '@/lib/auth';
import { useTheme } from '@/lib/theme';
import { SystemCompanion } from '@/components/companion/SystemCompanion';
import { NotificationBell } from '@/components/notifications/NotificationBell';

/** 導覽項目。與原生版 NAV 一一對應，adminOnly 的可見性規則相同。
 *  primary 標記的四項會留在手機版底部列，其餘收進「更多」。 */
export const NAV = [
  { to: '/overview', label: '總覽', Icon: LayoutDashboard, primary: true },
  { to: '/devices', label: '裝置', Icon: MonitorDot, primary: true },
  { to: '/events', label: '事件中心', Icon: ListTree, primary: true },
  { to: '/media', label: '檔案管理', Icon: FolderArchive, primary: true },
  { to: '/datasets', label: '研究資料集', Icon: Filter },
  { to: '/backup', label: '備份狀態', Icon: DatabaseBackup, adminOnly: true },
  { to: '/ota', label: '模型與韌體', Icon: Settings2, adminOnly: true },
  { to: '/sites', label: '場域管理', Icon: Map, adminOnly: true },
  { to: '/users', label: '帳號管理', Icon: ShieldCheck, adminOnly: true },
  { to: '/assistant', label: '小哨與 AI', Icon: Bot, adminOnly: true },
] as const;

const SUBTITLE: Record<string, string> = {
  '/overview': '平台整體狀態與今日重點',
  '/devices': '邊緣節點、場域與即時畫面',
  '/events': '偵測事件與人工判讀',
  '/media': '影音檔案的傳輸與保存狀態',
  '/datasets': '固定資料集與匯出紀錄',
  '/backup': '備份批次與還原演練',
  '/ota': '模型／韌體套件與分批部署',
  '/sites': '場域與裝置指派',
  '/users': '平台帳號與權限',
  '/assistant': 'AI 模型接入與異常監控門檻',
};

/* 響應式策略：
 *   ≥1025px  側欄 272px，圖示＋文字
 *   721~1024 側欄縮成 84px，只留圖示與極小字
 *   ≤720px   底部導覽列：只放 4 項常用 + 「更多」+ 小哨
 *            —— 9 項全擠進 390px 會讓每項不到 43px，文字被截斷，按不準 */
export default function AppShell() {
  const { user, logout } = useAuth();
  const { pathname } = useLocation();
  const [moreOpen, setMoreOpen] = useState(false);
  const [railOpen, setRailOpen] = useState(false);
  const [companionSlot, setCompanionSlot] = useState<HTMLDivElement | null>(null);
  const moreButtonRef = useRef<HTMLButtonElement>(null);
  const morePanelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!moreOpen) return;
    morePanelRef.current?.querySelector<HTMLElement>('a, button')?.focus();
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setMoreOpen(false);
        requestAnimationFrame(() => moreButtonRef.current?.focus());
      }
    };
    document.addEventListener('keydown', closeOnEscape);
    return () => document.removeEventListener('keydown', closeOnEscape);
  }, [moreOpen]);

  const { theme, cycle: cycleTheme } = useTheme();
  // 三態輪替：淺色 → 深色 → 跟隨系統。圖示直接表示「目前是什麼」，
  // 不用猜「按下去會變成什麼」。
  const ThemeIcon = theme === "dark" ? Moon : theme === "light" ? Sun : MonitorSmartphone;
  const themeTitle = theme === "dark" ? "外觀：深色（點選後切換為跟隨系統）"
    : theme === "light" ? "外觀：淺色（點選後切換為深色）"
    : "外觀：跟隨系統（點選後切換為淺色）";

  const items = NAV.filter((n) => !('adminOnly' in n && n.adminOnly) || user?.role === 'admin');
  const current = NAV.find((n) => pathname.startsWith(n.to));

  const primary = items.filter((n) => 'primary' in n && n.primary);
  const secondary = items.filter((n) => !('primary' in n && n.primary));
  const inMore = secondary.some((n) => pathname.startsWith(n.to));

  return (
    <div data-rail-open={railOpen} className={`app-shell min-h-[100dvh] md:grid ${railOpen ? 'md:grid-cols-[240px_minmax(0,1fr)]' : 'md:grid-cols-[92px_minmax(0,1fr)]'} lg:grid-cols-[272px_minmax(0,1fr)]`}>
      <a href="#main-content" className="fixed left-3 top-3 z-[100] -translate-y-20 rounded-sm bg-primary px-4 py-3 font-semibold text-primary-foreground shadow-lg transition-transform focus:translate-y-0">跳到主要內容</a>
      {/* ══ 側欄（md 以上）══
          sticky + h-screen：側欄必須固定在視窗高度內，不能跟著內容長。
          原本只有 min-h-screen，在長頁面（事件中心 6000px、OTA 3700px）
          會被 grid 拉成整頁高，底部的頭像與登出鈕就被推到畫面外，
          捲到頁尾才看得到 —— 看起來就像「不見了」。 */}
      <aside data-companion-edge className="sidebar-shell sticky top-0 hidden h-screen md:flex md:flex-col
                        md:border-r md:border-sidebar-line">
        <div className="flex items-center gap-3 px-6 py-6 md:justify-center md:px-2
                        lg:justify-start lg:px-6">
          {/* 深色側欄：CSS 提亮，不動原始 logo 檔案 */}
          <img src="/assets/ai-guard-logo.png" alt="AI-Guard"
               className="h-10 w-10 shrink-0 [filter:brightness(1.35)_saturate(1.15)]" />
          <div className={`${railOpen ? 'md:block' : 'md:hidden'} hidden leading-tight lg:block`}>
            <strong className="block font-display text-[17px] font-bold text-white">
              EdgeAI
            </strong>
            <span className="font-mono text-[9.5px] tracking-[0.16em] text-slate-500">
              FIELD CONSOLE
            </span>
          </div>
        </div>

        {/* 導覽區可捲：矮螢幕（例如 768px 高）放不下 9 項時，
            讓它自己捲，而不是把底部的頭像與登出鈕擠出可視範圍。 */}
        <nav className="flex min-h-0 flex-1 flex-col gap-1 overflow-y-auto px-2 py-2 lg:px-4">
          {items.map(({ to, label, Icon }) => (
            <NavLink key={to} to={to} data-active={pathname.startsWith(to)}
                     title={!railOpen ? label : undefined}
                     className={`sidebar-item justify-center gap-1 px-1 text-center
                                md:!min-h-[60px]
                                lg:min-h-0 lg:flex-row lg:justify-start lg:gap-3
                                lg:px-4 lg:py-3 lg:text-left ${railOpen ? 'md:flex-row md:justify-start md:gap-3 md:px-4 md:text-left' : 'md:flex-col'}`}>
              <Icon className="h-[18px] w-[18px] shrink-0" />
              <span className={`${railOpen ? 'md:text-sm' : 'md:text-[11px]'} min-w-0 truncate leading-snug lg:text-sm`}>{label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="border-t border-sidebar-line p-4">
          {/* 窄側欄容得下 40px 圓鈕，改成直向堆疊，
              不要等到 lg 才顯示登出 —— 中間斷點會變成兩邊都沒有。 */}
          <div className={`flex items-center gap-2 lg:flex-row lg:justify-start lg:gap-3 ${railOpen ? 'md:flex-row md:justify-start' : 'flex-col'}`}>
            <div className="grid h-9 w-9 shrink-0 place-items-center rounded-sm bg-primary
                            text-sm font-bold text-white">
              {(user?.display_name || user?.username || '?').slice(0, 1).toUpperCase()}
            </div>
            <div className={`${railOpen ? 'md:block' : 'md:hidden'} hidden min-w-0 flex-1 leading-tight lg:block`}>
              <p className="truncate text-[13px] font-semibold text-slate-100">
                {user?.display_name || user?.username}
              </p>
              <p className="text-[11px] text-slate-500">{roleLabel(user?.role)}</p>
            </div>
            <button onClick={cycleTheme} title={themeTitle} aria-label={themeTitle}
                    className="grid h-10 w-10 shrink-0 place-items-center rounded-sm
                               text-slate-400 transition-colors hover:bg-white/5
                               hover:text-slate-100">
              {/* key=theme：換主題時圖示重新掛載，重播一次 icon-swap 旋轉淡入 */}
              <ThemeIcon key={theme} className="icon-swap h-[18px] w-[18px]" />
            </button>
            <button onClick={() => void logout()} title="登出"
                    className="grid h-10 w-10 shrink-0 place-items-center rounded-sm
                               text-slate-400 transition-colors hover:bg-white/5
                               hover:text-slate-100">
              <LogOut className="h-[18px] w-[18px]" />
            </button>
          </div>
        </div>
      </aside>

      {/* ══ 內容區 ══ */}
      <div className="flex min-w-0 flex-col pb-[calc(64px+env(safe-area-inset-bottom))] md:pb-0">
        {/* sticky：標題列上放著登出鈕（lg 以下側欄沒有位置擺），
            捲動時必須跟著，否則長頁面捲到中段就按不到登出。 */}
        <header className="sticky top-0 z-30 flex items-center justify-between gap-4
                           border-b border-border bg-card px-5 py-4 md:px-8 md:py-5">
          <div className="flex min-w-0 items-start gap-3">
            <button type="button" onClick={() => setRailOpen((v) => !v)}
                    aria-label={railOpen ? '收合側邊導覽' : '展開側邊導覽'}
                    title={railOpen ? '收合側邊導覽' : '展開側邊導覽'}
                    className="mt-0.5 hidden h-11 w-11 shrink-0 place-items-center rounded-sm border border-border text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground md:grid lg:hidden">
              {railOpen ? <PanelLeftClose className="h-[18px] w-[18px]" /> : <PanelLeftOpen className="h-[18px] w-[18px]" />}
            </button>
            <div className="min-w-0">
            <p className="font-mono text-[10px] tracking-[0.16em] text-muted-foreground">
              OPERATIONS
            </p>
            {/* key=路徑：換頁時標題與副標重新掛載，重播 title-swap（副標慢 40ms） */}
            <h1 key={current?.to ?? 'home'} className="title-swap mt-1 truncate font-display text-[19px] font-bold tracking-tight md:text-[22px]">
              {current?.label ?? 'EdgeAI'}
            </h1>
            <p key={`${current?.to ?? 'home'}-sub`} className="title-swap title-swap--sub mt-1 text-[13px] leading-relaxed text-muted-foreground">
              {SUBTITLE[current?.to ?? ''] ?? ''}
            </p>
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <NotificationBell />
            {/* 只在手機顯示：md 以上側欄本身就有登出鈕，免得出現兩顆 */}
            <button onClick={() => void logout()} title="登出" aria-label="登出"
                    className="grid h-11 w-11 shrink-0 place-items-center rounded-sm border
                               border-border text-muted-foreground transition-colors
                               hover:text-foreground md:hidden">
              <LogOut className="h-[18px] w-[18px]" />
            </button>
          </div>
        </header>

        {/* md 以上小哨是可拖曳的卡片，預設在右下角（底 24px，卡片 64px 加上探出的頭約 78px）。
            下方多留 144px，捲到底時「下一頁」等頁尾按鈕不會被蓋住；拖到其他位置時由使用者自己決定。
            xl:p-8 會覆寫 md 的設定，所以 xl 要再寫一次 */}
        <main id="main-content" tabIndex={-1} className="mx-auto w-full min-w-0 max-w-[1600px] flex-1 bg-background p-4 md:p-6 md:pb-36 xl:p-8 xl:pb-36">
          <SystemCompanion key={user?.username} navSlot={companionSlot} onNavOpen={() => setMoreOpen(false)} />
          {/* key=pathname：換頁時整個內容區重播一次 page-enter 進場 */}
          <div key={pathname} className="page-enter">
            <Outlet />
          </div>
        </main>
      </div>

      {/* ══ 手機底部導覽（md 以下）══
          w-screen + max-w-full：inset-x-0 的寬度取決於「包含塊」，而祖先只要
          有 transform/filter/contain 之類的屬性（例如某些頁面的動畫容器），
          fixed 元素就會改以那個祖先為基準，欄寬算出來比螢幕還寬而溢出。
          直接綁螢幕寬度就不受祖先影響。
          auto-cols-fr 只保證等寬，不會壓過格子的最小內容寬度，所以每格
          另外加 min-w-0、標籤加 truncate，長標籤才不會把格子撐開。 */}
      <nav className="sidebar-shell nav-bottom fixed inset-x-0 bottom-0 z-50 grid w-screen max-w-full
                      grid-flow-col auto-cols-fr border-t border-sidebar-line
                      pb-[env(safe-area-inset-bottom)] md:hidden">
        {primary.map(({ to, label, Icon }) => (
          <NavLink key={to} to={to} data-active={pathname.startsWith(to)}
                   onClick={() => setMoreOpen(false)}
                   className="sidebar-item !min-h-[64px] min-w-0 flex-col justify-center
                              gap-1 rounded-none !px-1 text-center">
            <Icon className="h-[21px] w-[21px] shrink-0" />
            <span className="w-full truncate text-[11px] leading-tight">{label}</span>
          </NavLink>
        ))}
        {secondary.length > 0 && (
          <button ref={moreButtonRef} onClick={() => setMoreOpen((v) => !v)} data-active={inMore || moreOpen} aria-expanded={moreOpen} aria-controls="mobile-more" aria-haspopup="dialog"
                  className="sidebar-item !min-h-[64px] min-w-0 flex-col justify-center
                             gap-1 rounded-none !px-1 text-center">
            {moreOpen ? <X className="h-[21px] w-[21px]" />
                      : <MoreHorizontal className="h-[21px] w-[21px]" />}
            <span className="w-full truncate text-[11px] leading-tight">更多</span>
          </button>
        )}
        {/* 小哨的入口由 SystemCompanion 用 portal 放進來，狀態和面板都在那邊。
            放最右邊，和桌機浮動小哨的位置一致。 */}
        <div ref={setCompanionSlot} className="flex min-w-0" />
      </nav>

      {/* 「更多」展開的面板 */}
      {moreOpen && (
        <>
          <div className="drawer-overlay fixed inset-0 z-40 bg-slate-900/45 md:hidden"
               onClick={() => setMoreOpen(false)} />
          <div ref={morePanelRef} id="mobile-more" role="dialog" aria-modal="true" aria-label="更多導覽" className="sheet-up fixed inset-x-0 bottom-[calc(64px+env(safe-area-inset-bottom))]
                          z-50 rounded-t-[20px] border-t border-sidebar-line
                          bg-sidebar p-3 md:hidden">
            {/* 目前登入身分：側欄的名字區塊在 md 以下不顯示，
                手機上就完全看不到自己是誰。放在這裡不佔常駐版面。 */}
            <div className="mb-3 flex items-center gap-3 px-2">
              <div className="grid h-9 w-9 shrink-0 place-items-center rounded-full
                              bg-primary/20 text-[13px] font-semibold text-slate-100">
                {(user?.display_name || user?.username || '?').slice(0, 1).toUpperCase()}
              </div>
              <div className="min-w-0">
                <p data-user-name className="truncate text-[13px] font-medium text-slate-100">
                  {user?.display_name || user?.username}
                </p>
                <p className="text-[11px] text-slate-500">{roleLabel(user?.role)}</p>
              </div>
            </div>
            <div className="nav-bottom stagger grid grid-cols-3 gap-2">
              {secondary.map(({ to, label, Icon }) => (
                <NavLink key={to} to={to} data-active={pathname.startsWith(to)}
                         onClick={() => setMoreOpen(false)}
                         className="sidebar-item !min-h-[76px] flex-col justify-center
                                    gap-1.5 text-center">
                  <Icon className="h-[22px] w-[22px]" />
                  <span className="text-[11px] leading-tight">{label}</span>
                </NavLink>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function roleLabel(r?: string) {
  return r === 'admin' ? '系統管理員' : r === 'researcher' ? '研究人員' : '唯讀使用者';
}
